import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import { findIndex } from 'lodash';

import useSnackbar from '@joinforma/ui/hooks/useSnackbar';

import CurrentUserContext from 'contexts/CurrentUserContext';
import FeatureFlagContext from 'contexts/FeatureFlagContext';

import useQueryCipApplication from 'hooks/useQueryCipApplication';
import useQueryAlegeusPretaxAccounts from 'hooks/useQueryPretaxAccounts';
import useQueryPretaxDependents from 'hooks/useQueryPretaxDependents';
import useQueryPretaxV2Dependent from 'hooks/useQueryPretaxV2Dependents';
import useQueryPretaxEligibleWallets from 'hooks/useQueryPretaxEligibleWallets';
import useQueryHsaBulkTransfer from 'hooks/useQueryHsaBulkTransfer';
import useMutationHsaBulkTransfer from 'hooks/useMutationHsaBulkTransfer';

import {
  ENROLL_PENDING_PRETAX_COMMUTER_ACCOUNT,
  PretaxAccountV2Type,
  PretaxCommuterEnrollStatus,
} from 'constants/accounts';
import { FeatureFlag } from 'constants/featureFlag';
import {
  ALEGEUS_GROUP_TWO,
  MemberHsaTransferType,
  SHOW_ALEGEUS_HSA_RETAIL_ELEMENTS_DATE,
} from 'constants/hsaBulkTransfer';

import { isPretaxWallet, isHSATypeAccount } from 'utils/pretaxAccount';

import AlegeusModal from './AlegeusHSABulkTransferModal';
import NonAlegeusModal from './NonAlegeusHSABulkTransferModal';

import {
  recordSprigPretaxAttributes,
  SprigPretaxAttributes,
} from 'utils/sprig';

import {
  PretaxAccount,
  PretaxV2Dependent,
  EmployeeWallet,
  IPretaxContext,
} from './types';
import dayjs from 'utils/date-helper';

const determineHasPretaxWallets = (pretaxAccounts: Array<unknown>) =>
  Array.isArray(pretaxAccounts) && pretaxAccounts.length > 0;

const determineIfMemberHasAlegeusHsaInvestmentBalance = ({
  status,
  alegeusTotalHsaBalance,
  alegeusCashHsaBalance,
}: {
  status: MemberHsaTransferType | null;
  alegeusTotalHsaBalance: number;
  alegeusCashHsaBalance: number;
}): boolean => {
  // No Liquidation means alegeus told us that the user did not liquidate their hsa investments
  if (status === MemberHsaTransferType.NoLiquidation) {
    return true;
  }

  /**
   * Total HSA Balance = Portfolio + Cash
   * Portfolio Balance = Total - Cash
   * If the portfolio balance is greater than $0.99, we consider them to have an invested balance
   * and <= $0.99, we consider them to not have an invested balance due to residual value
   */
  return (
    Number((alegeusTotalHsaBalance - alegeusCashHsaBalance).toFixed(2)) > 0.99
  );
};

const PretaxContext = createContext<IPretaxContext>({
  alegeus_pretax_accounts: [],
  alegeus_pretax_dependents: [],
  isLoadingAlegeusPretax: true,
  isUpdatingWallets: true,
  new_pretax_experience: false,
  pretax_accounts: [],
  pretax_dependents: [],
  formaCommuterEligible: false,
  pretaxEligibleWallets: [],
  updateWallets: () => {},
  refetchPretaxV1Dependents: () => {},
  isAlegeusHSA: false,
  hsaBulkTransferData: {
    openModal: () => {},
    alegeusHsaAccountId: null,
    refetchMemberHsaBulkTransferDecision: () => {},
    isEligible: false,
    memberStatus: null,
    custodianName: '',
    optInStartDate: '',
    transferDeadline: '',
    fundsTransferDate: '',
    termsConditionsLink: '',
    memberHasFormaHSA: false,
    isMemberAlegeusGroupTwo: false,
    memberHasAlegeusHsaInvestmentBalance: false,
    memberHasAlegeusRetailHsa: false,
  },
});

export const PretaxContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isAlegeusModalOpen, setIsAlegeusModalOpen] = React.useState(false);
  const [isNonAlegeusModalOpen, setIsNonAlegeusModalOpen] =
    React.useState(false);

  const [isUpdatingWallets, setIsUpdatingWallets] = useState(true);
  const [employee_wallets, setEmployeeWallets] = useState<
    Array<EmployeeWallet>
  >([]);
  const hasV2PretaxStatusBeenRecorded = useRef<boolean>(false);
  const userHasntSeenHsaModalInCurrentSession = useRef(true);
  const { showSuccessSnackbar } = useSnackbar();

  const {
    employee: {
      id: employeeId,
      is_profile_complete,
      settings: {
        is_cdh_enabled,
        is_pretax_enabled,
        pretax_commuter_status = PretaxCommuterEnrollStatus.SELECTION_DISABLED,
      },
      impersonated,
    },
    company: {
      id,
      features: { pretax_config = { twic_commuter: false } },
      settings: { hsaBulkTransfer },
    },
  } = useContext(CurrentUserContext);

  const hasHsaAccount =
    employee_wallets.filter(
      (wallet) => wallet.wallet_type_id === PretaxAccountV2Type.HSA
    ).length > 0;

  const { data: cipApplicationData } = useQueryCipApplication({
    enabled: hasHsaAccount,
  });

  const bankApplicationStatus = cipApplicationData?.bankApplicationStatus;

  const handleCloseHSAConsolidateModal = () => {
    setIsAlegeusModalOpen(false);
    setIsNonAlegeusModalOpen(false);
  };

  const handleOpenHSAConsolidateModal = useCallback(() => {
    const isHsaCustodianAlegeus = hsaBulkTransfer?.custodianName === 'alegeus';

    if (isHsaCustodianAlegeus) {
      setIsAlegeusModalOpen(true);
      return;
    }

    setIsNonAlegeusModalOpen(true);
  }, [hsaBulkTransfer]);

  const featureFlag = useContext(FeatureFlagContext);
  const pretaxV2Enabled: boolean = featureFlag.flag(
    FeatureFlag.FORMA_PRETAX_ACCOUNTS_CLAIMS_PAGE,
    false
  );

  const isAlegeusHSAEnabled = featureFlag.flag(
    FeatureFlag.ALEGEUS_HSA_ENABLED,
    false
  );

  const sprigUserResearchEnabled: boolean = featureFlag.flag(
    FeatureFlag.SPRIG_USER_RESEARCH,
    false
  );

  const memberIdsAlegeusRetailHsa: Array<string> = featureFlag.flag(
    FeatureFlag.ALEGEUS_RETAIL_HSA,
    []
  );

  const memberHasAlegeusRetailHsa = memberIdsAlegeusRetailHsa.length
    ? memberIdsAlegeusRetailHsa.includes(employeeId)
    : false;

  const newPretaxExperience: boolean = impersonated
    ? localStorage.getItem('pretax_v2') === 'true'
    : pretaxV2Enabled;

  // A few customers are still on the old pretax experience
  const shouldFetchAlegeusAccountsV1 = is_cdh_enabled && !newPretaxExperience;

  // cutting off this request in V2 on SHOW_ALEGEUS_HSA_RETAIL_ELEMENTS
  const shouldFetchAlegeusAccountsV2 =
    isAlegeusHSAEnabled &&
    dayjs().isBefore(SHOW_ALEGEUS_HSA_RETAIL_ELEMENTS_DATE);

  const {
    data: alegeus_pretax_accounts_data,
    isLoading: is_pretax_accounts_loading,
  } = useQueryAlegeusPretaxAccounts({
    enabled: shouldFetchAlegeusAccountsV1 || shouldFetchAlegeusAccountsV2,
    onSuccess: ({ pretax_accounts }) =>
      sprigUserResearchEnabled &&
      recordSprigPretaxAttributes({
        hasPretaxWallets: determineHasPretaxWallets(pretax_accounts),
      }),
  });
  const {
    data: pretax_dependents_data,
    isLoading: is_pretax_dependents_loading,
    refetch: refetchPretaxV1Dependents,
  } = useQueryPretaxDependents({
    is_cdh_enabled: is_cdh_enabled && !newPretaxExperience,
  });

  const handleUpdateEmployeeWallets = (wallets: Array<EmployeeWallet>) => {
    setIsUpdatingWallets(true);
    /**
     * This block should only ever once per mount. It records to Sprig whether
     * user has any v2 pretax accounts _after_ the data has been fetched.
     */
    if (!hasV2PretaxStatusBeenRecorded.current) {
      const pretaxWallets = wallets.filter(isPretaxWallet);

      recordSprigPretaxAttributes({
        hasPretaxWallets: pretaxWallets.length > 0,
      });

      hasV2PretaxStatusBeenRecorded.current = true;
    }

    setEmployeeWallets(wallets);
    setIsUpdatingWallets(false);
  };

  const { data: pretaxv2_dependents_data } = useQueryPretaxV2Dependent({
    is_pretax_enabled: newPretaxExperience,
  });

  const formaCommuterEligible = Boolean(
    is_pretax_enabled &&
      pretax_commuter_status !== PretaxCommuterEnrollStatus.SELECTION_DISABLED
  );

  // Right now we only support commuter accounts, so enable this query
  // when user is commuter eligible for performance purpose.
  const { data: eligibleWalletsData } = useQueryPretaxEligibleWallets({
    enabled: formaCommuterEligible && newPretaxExperience,
    formaEnrollment: true,
  });

  const alegeus_pretax_accounts = useMemo(
    () => alegeus_pretax_accounts_data?.pretax_accounts ?? [],
    [alegeus_pretax_accounts_data]
  );

  const alegeusHSA = alegeus_pretax_accounts.filter(
    (account) =>
      isHSATypeAccount(account.account_type) &&
      ['active', 'new'].includes(account?.account_status_code?.toLowerCase())
  );

  const alegeusCashHsaBalance = alegeusHSA[0]
    ? alegeusHSA[0].account_detail_info.available_balance
    : 0;

  const alegeusTotalHsaBalance = alegeusHSA[0]
    ? alegeusHSA[0].total_hsa_balance
    : 0;

  const pretax_accounts = useMemo(
    () => employee_wallets.filter(isPretaxWallet) || [],
    [employee_wallets]
  );

  const pretax_dependents: Array<PretaxV2Dependent> =
    pretaxv2_dependents_data?.pretax_dependents || [];

  // could be undefined if member has no Forma HSA
  const [potentialFormaHSA] = pretax_accounts.filter(
    ({ wallet_type_id }) => wallet_type_id === PretaxAccountV2Type.HSA
  );

  const doesUserHaveFormaHsa = Boolean(potentialFormaHSA);

  // Query that answers if the member has opted-in or out
  const {
    data: memberHsaBulkTransferData,
    isLoading: isMemberHsaBulkTransferDecisionLoading,
    refetch: refetchMemberHsaBulkTransferDecision,
  } = useQueryHsaBulkTransfer({
    enabled:
      Boolean(hsaBulkTransfer) &&
      (Boolean(alegeusHSA.length) ||
        doesUserHaveFormaHsa ||
        memberHasAlegeusRetailHsa),
  });

  const memberDidNotAcceptTerms = Boolean(
    memberHsaBulkTransferData?.data?.transferType !==
      MemberHsaTransferType.Accept
  );

  const shouldHideBulkTransfer = Boolean(
    memberHsaBulkTransferData?.data?.transferType === MemberHsaTransferType.Hide
  );

  const { mutate: updateNonAlegeusMemberTermsDecision } =
    useMutationHsaBulkTransfer({
      onSuccess: () => {
        showSuccessSnackbar({
          text: `Great! You're all set, we'll transfer your HSA to Forma!`,
        });
        handleCloseHSAConsolidateModal();
        refetchMemberHsaBulkTransferDecision();
      },
    });

  const memberCompletedOnboarding = is_profile_complete;

  const alegeusHsaAccountId = alegeusHSA[0]?.flex_account_id
    ? Number(alegeusHSA[0].flex_account_id)
    : null;

  const doesUserHaveAlegeusHSA =
    alegeusHSA.length > 0 || memberHasAlegeusRetailHsa;

  const isMemberWithinOptInWindow = hsaBulkTransfer
    ? dayjs().isBetween(
        dayjs(hsaBulkTransfer.optInStartDate),
        dayjs(hsaBulkTransfer.transferDeadline),
        'day',
        '[]'
      )
    : false;

  const isMemberAlegeusGroupTwo = ALEGEUS_GROUP_TWO.includes(id);

  const isMemberEligibleForHsaBulkTransfer = hsaBulkTransfer
    ? hsaBulkTransfer.custodianName === 'alegeus'
      ? memberCompletedOnboarding &&
        memberHsaBulkTransferData !== undefined &&
        doesUserHaveAlegeusHSA &&
        !shouldHideBulkTransfer
      : memberCompletedOnboarding &&
        memberHsaBulkTransferData !== undefined &&
        doesUserHaveFormaHsa &&
        !shouldHideBulkTransfer
    : false;

  const shouldMemberSeeBulkTransferModal =
    isMemberEligibleForHsaBulkTransfer &&
    userHasntSeenHsaModalInCurrentSession &&
    memberDidNotAcceptTerms &&
    isMemberWithinOptInWindow;

  useEffect(() => {
    if (shouldMemberSeeBulkTransferModal) {
      userHasntSeenHsaModalInCurrentSession.current = false;
      handleOpenHSAConsolidateModal();
    }
  }, [handleOpenHSAConsolidateModal, shouldMemberSeeBulkTransferModal]);

  /**
   * We need to record this earlier and separately from "has pretax wallets"
   * as the # of pretax wallets is only fetched if pretax enabled flag is
   * true.
   */
  useEffect(() => {
    const payload: SprigPretaxAttributes = {
      isPretaxEnabled: is_pretax_enabled ?? false,
    };

    /**
     * Obviously pretax disabled members cannot have pretax wallets (right??) so
     * we can eagerly record this scenario _just this once_.
     */
    if (!is_pretax_enabled) {
      payload.hasPretaxWallets = false;
      hasV2PretaxStatusBeenRecorded.current = true;
    }

    recordSprigPretaxAttributes(payload);
  }, [is_pretax_enabled]);

  if (newPretaxExperience) {
    return (
      <PretaxContext.Provider
        value={{
          bankApplicationStatus,
          pretax_accounts,
          pretax_dependents,
          isUpdatingWallets,
          isLoadingAlegeusPretax:
            is_pretax_accounts_loading ||
            isMemberHsaBulkTransferDecisionLoading,
          new_pretax_experience: true,
          alegeus_pretax_accounts: alegeusHSA,
          alegeus_pretax_dependents: [],
          formaCommuterEligible,
          pretaxEligibleWallets: eligibleWalletsData?.eligibleWallets || [],
          updateWallets: handleUpdateEmployeeWallets,
          isAlegeusHSA: isAlegeusHSAEnabled,
          hsaBulkTransferData:
            hsaBulkTransfer && isMemberEligibleForHsaBulkTransfer
              ? {
                  openModal: () => handleOpenHSAConsolidateModal(),
                  memberStatus:
                    memberHsaBulkTransferData?.data?.transferType ?? null,
                  alegeusHsaAccountId,
                  refetchMemberHsaBulkTransferDecision,
                  memberHasFormaHSA: doesUserHaveFormaHsa,
                  isMemberAlegeusGroupTwo,
                  memberHasAlegeusHsaInvestmentBalance:
                    determineIfMemberHasAlegeusHsaInvestmentBalance({
                      status:
                        memberHsaBulkTransferData?.data?.transferType ?? null,
                      alegeusTotalHsaBalance,
                      alegeusCashHsaBalance,
                    }),
                  memberHasAlegeusRetailHsa,
                  ...hsaBulkTransfer,
                }
              : undefined,
        }}
      >
        {children}
        {hsaBulkTransfer ? (
          isAlegeusModalOpen ? (
            <AlegeusModal
              isOpen={isAlegeusModalOpen}
              onClose={handleCloseHSAConsolidateModal}
            />
          ) : isNonAlegeusModalOpen && doesUserHaveFormaHsa ? (
            <NonAlegeusModal
              isOpen={isNonAlegeusModalOpen}
              onClose={handleCloseHSAConsolidateModal}
              custodianName={hsaBulkTransfer.custodianName}
              transferDeadline={hsaBulkTransfer.transferDeadline}
              termsAndConditionsLink={hsaBulkTransfer.termsConditionsLink}
              handleOptIn={updateNonAlegeusMemberTermsDecision}
              hsaAccountId={potentialFormaHSA.id}
            />
          ) : null
        ) : null}
      </PretaxContext.Provider>
    );
  }

  const alegeus_pretax_dependents =
    pretax_dependents_data?.pretax_dependents || [];
  const commuter_pending_enroll =
    pretax_config.twic_commuter &&
    pretax_commuter_status === PretaxCommuterEnrollStatus.SELECTION_PENDING;
  if (
    commuter_pending_enroll &&
    findIndex(
      alegeus_pretax_accounts,
      (a: PretaxAccount) =>
        a.flex_account_id ===
        ENROLL_PENDING_PRETAX_COMMUTER_ACCOUNT.flex_account_id
    ) === -1
  ) {
    alegeus_pretax_accounts.push(
      ENROLL_PENDING_PRETAX_COMMUTER_ACCOUNT as PretaxAccount
    );
  }

  return (
    <PretaxContext.Provider
      value={{
        alegeus_pretax_accounts,
        alegeus_pretax_dependents,
        isUpdatingWallets,
        isLoadingAlegeusPretax:
          is_pretax_accounts_loading || is_pretax_dependents_loading,
        new_pretax_experience: false,
        pretax_accounts: [],
        pretax_dependents: [],
        formaCommuterEligible: false,
        pretaxEligibleWallets: [],
        updateWallets: () => {},
        refetchPretaxV1Dependents,
        // Doesn't affect old experience
        isAlegeusHSA: false,
      }}
    >
      {children}
    </PretaxContext.Provider>
  );
};

export default PretaxContext;
